import { cloneElement, type FC, type ReactElement } from 'react';
import { FormProvider, useForm } from 'react-hook-form@latest';

import type { RequestDraft } from '@/components/Glossary/Sections/Glossary.model';
import Assignees from '@/components/Glossary/Sections/Requests/Create/Summary/Assignees';
import Banner from '@/components/UI/Banner';
import FormRender from '@/components/UI/FormRender';
import Summary, { SummaryItem } from '@/components/UI/Summary';
import type { RequestTemplateGroups } from '@/model/Request.model';

interface RequestSummaryProps {
  ActionButtons: ReactElement<{ onOk?: () => void }>;
  onSubmit: () => void;
  request: RequestDraft;
}

const RequestSummary: FC<RequestSummaryProps> = ({ ActionButtons, onSubmit, request }) => {
  const formMethods = useForm({
    defaultValues: {
      fields: request.form,
    },
  });

  // a field's "name" either comes from the API if already set, or else it needs assigned by the frontend.
  // it becomes the property on the form's POST payload.
  const groupsWithNames = (request.form?.groups ?? []).map((group, groupIdx) => ({
    ...group,
    fields: Array.isArray(group.fields)
      ? group.fields.map((field, fieldIdx) => ({ ...field, name: `groups[${groupIdx}].fields[${fieldIdx}].value` }))
      : [],
  }));

  return (
    <div data-test-component="Summary" data-test-element="container">
      <div className="mb4" style={{ maxWidth: 900 }}>
        <Summary title="Summary">
          <SummaryItem label="Request name">{request.name}</SummaryItem>
          <SummaryItem label="Assignees">
            <Assignees assignees={request.users} />
          </SummaryItem>
        </Summary>
      </div>
      <div className="pa3 ui-card" style={{ width: 550 }}>
        <Banner info className="mw6 mb4" dismiss={false} center>
          <h3 className="f6 normal lh-title">This is a preview of your form - it can&apos;t be submitted.</h3>
        </Banner>
        <FormProvider {...formMethods}>
          <FormRender groups={groupsWithNames as unknown as Array<RequestTemplateGroups>} hookFormVersion="latest" />
        </FormProvider>
      </div>

      {cloneElement(ActionButtons, { onOk: () => onSubmit() })}
    </div>
  );
};

export default RequestSummary;
